import type { TagReference } from "@/domains/documents/components/molecules/DocumentTags";
import type { Language } from "@/lib/i18n/types";

interface CategoryLike {
  id: number;
  name: string;
  name_et?: string | null;
}

interface DisciplineLike {
  id: number;
  name: string;
  name_en?: string | null;
  description?: string | null;
}

interface StageLike {
  id: number;
  name: string;
  name_en?: string | null;
}

export interface TaggedDocumentLike {
  categories?: readonly CategoryLike[] | null;
  design_disciplines?: readonly DisciplineLike[] | null;
  design_stages?: readonly StageLike[] | null;
}

/**
 * The tag pills a document list row shows: its categories (purple),
 * design disciplines (yellow) and design stages (green), in the viewer's
 * language. Folder and project are deliberately not pills — the folder path
 * is the filename's hover tooltip.
 *
 * Tag names are localised asymmetrically in the database: categories are
 * canonically English (`name_et` translates), disciplines and stages
 * canonically Estonian (`name_en` translates).
 */
function categoryName(category: CategoryLike, language: Language): string {
  return language === "et" ? category.name_et?.trim() || category.name : category.name;
}

function categoryTags(categories: readonly CategoryLike[], language: Language): TagReference[] {
  const seen = new Set<string>();
  return categories
    .filter((category) => {
      const name = category.name?.trim().toLowerCase();
      if (!name || name.includes("other") || seen.has(name)) return false;
      seen.add(name);
      return true;
    })
    .map<TagReference>((category) => ({
      type: "category",
      id: `category-${category.id}`,
      name: categoryName(category, language),
    }));
}

function disciplineName(discipline: DisciplineLike, language: Language): string {
  if (language !== "en") return discipline.name;
  return discipline.name_en?.trim() || discipline.description?.trim() || discipline.name;
}

function disciplineTags(
  disciplines: readonly DisciplineLike[],
  language: Language
): TagReference[] {
  return disciplines
    .map<TagReference>((discipline) => ({
      type: "construction",
      id: `construction-${discipline.id}`,
      name: disciplineName(discipline, language),
    }))
    .filter((tag) => Boolean(tag.name?.trim()));
}

function stageTags(stages: readonly StageLike[], language: Language): TagReference[] {
  return stages
    .map<TagReference>((stage) => ({
      type: "stage",
      id: `stage-${stage.id}`,
      name: language === "en" ? stage.name_en?.trim() || stage.name : stage.name,
    }))
    .filter((tag) => Boolean(tag.name?.trim()));
}

export function documentTagReferences(doc: TaggedDocumentLike, language: Language): TagReference[] {
  return [
    ...categoryTags(doc.categories ?? [], language),
    ...disciplineTags(doc.design_disciplines ?? [], language),
    ...stageTags(doc.design_stages ?? [], language),
  ];
}
